import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box, Paper, Stack, Typography, Button, IconButton, Chip, Divider, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Switch,
  FormControlLabel, CircularProgress, Alert, Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import { GET_ADVANCED_MENUS } from '../../apollo/advancedMenuQueries'

const CLOSURE_FIELDS = `
  _id
  menuId
  menuName
  name
  startDate
  endDate
  allDay
  startTime
  endTime
  note
  dateLabel
  isActiveNow
`

const GET_CLOSURES = gql`
  query RestaurantClosures($restaurantId: ID!, $includePast: Boolean) {
    restaurantClosures(restaurantId: $restaurantId, includePast: $includePast) {
      ${CLOSURE_FIELDS}
    }
  }
`

const CREATE_CLOSURE = gql`
  mutation CreateRestaurantClosure($restaurantId: ID!, $input: RestaurantClosureInput!) {
    createRestaurantClosure(restaurantId: $restaurantId, input: $input) { ${CLOSURE_FIELDS} }
  }
`

const UPDATE_CLOSURE = gql`
  mutation UpdateRestaurantClosure($id: ID!, $input: RestaurantClosureInput!) {
    updateRestaurantClosure(id: $id, input: $input) { ${CLOSURE_FIELDS} }
  }
`

const DELETE_CLOSURE = gql`
  mutation DeleteRestaurantClosure($id: ID!) {
    deleteRestaurantClosure(id: $id)
  }
`

const blankForm = () => {
  const today = new Date().toISOString().slice(0, 10)
  return {
    name: '', startDate: today, endDate: today, allDay: true,
    startTime: '', endTime: '', menuId: '', note: '',
  }
}

/**
 * Holidays and planned closings.
 *
 * A closure with no menu shuts the whole restaurant for those dates — the
 * storefront shows closed and orders scheduled into the range are refused. Pick
 * a menu instead to close only that menu (no brunch on Dec 24, dinner as usual).
 */
const ClosuresPanel = ({ restaurantId }) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(blankForm())
  const [error, setError] = useState('')
  const [showPast, setShowPast] = useState(false)

  const variables = { restaurantId, includePast: showPast }
  const { data, loading } = useQuery(GET_CLOSURES, { variables, skip: !restaurantId, fetchPolicy: 'network-only' })
  const { data: menusData } = useQuery(GET_ADVANCED_MENUS, { variables: { restaurantId }, skip: !restaurantId })
  const menus = menusData?.advancedMenus || []
  const closures = data?.restaurantClosures || []

  const refetchQueries = [{ query: GET_CLOSURES, variables }]
  const onError = (err) => setError(err.message)
  const [createClosure, { loading: creating }] = useMutation(CREATE_CLOSURE, {
    refetchQueries, onCompleted: () => setDialogOpen(false), onError,
  })
  const [updateClosure, { loading: updating }] = useMutation(UPDATE_CLOSURE, {
    refetchQueries, onCompleted: () => setDialogOpen(false), onError,
  })
  const [deleteClosure] = useMutation(DELETE_CLOSURE, { refetchQueries, onError })

  const saving = creating || updating
  const setField = (patch) => setForm(prev => ({ ...prev, ...patch }))

  const openCreate = () => { setEditing(null); setForm(blankForm()); setError(''); setDialogOpen(true) }
  const openEdit = (c) => {
    setEditing(c)
    setForm({
      name: c.name || '',
      startDate: c.startDate || '',
      endDate: c.endDate || c.startDate || '',
      allDay: c.allDay !== false,
      startTime: c.startTime || '',
      endTime: c.endTime || '',
      menuId: c.menuId || '',
      note: c.note || '',
    })
    setError('')
    setDialogOpen(true)
  }

  const handleSave = () => {
    setError('')
    if (!form.name.trim()) { setError('Give the closing a name, e.g. "Christmas Day".'); return }
    if (!form.startDate) { setError('Pick a start date.'); return }
    const endDate = form.endDate || form.startDate
    if (endDate < form.startDate) { setError('The end date cannot be before the start date.'); return }
    if (!form.allDay && (!form.startTime || !form.endTime)) {
      setError('A part-day closing needs both a start time and an end time.'); return
    }
    const input = {
      name: form.name.trim(),
      startDate: form.startDate,
      endDate,
      allDay: form.allDay,
      startTime: form.allDay ? null : form.startTime,
      endTime: form.allDay ? null : form.endTime,
      menuId: form.menuId || null,
      note: form.note.trim() || null,
    }
    if (editing) updateClosure({ variables: { id: editing._id, input } })
    else createClosure({ variables: { restaurantId, input } })
  }

  const handleDelete = (c) => {
    if (window.confirm(`Delete "${c.name}"? The restaurant will take orders on those dates again.`)) {
      deleteClosure({ variables: { id: c._id } })
    }
  }

  return (
    <Paper elevation={0} sx={{ mt: 2.5, borderRadius: '16px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2 }}>
        <EventBusyIcon sx={{ color: '#dc2626' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>Holidays &amp; Closings</Typography>
          <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>
            Schedule closings ahead of time. On these dates the storefront shows closed and orders cannot be placed or scheduled.
          </Typography>
        </Box>
        <Button size="small" onClick={() => setShowPast(p => !p)} sx={{ textTransform: 'none', color: '#64748b' }}>
          {showPast ? 'Hide past' : 'Show past'}
        </Button>
        <Button
          variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', backgroundColor: '#1e40af' }}
        >
          Add closing
        </Button>
      </Box>
      <Divider />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
      ) : closures.length === 0 ? (
        <Typography sx={{ px: 2.5, py: 3, color: '#94a3b8', fontSize: '0.9rem' }}>
          No closings scheduled. Add one for holidays, vacation weeks, or a private event.
        </Typography>
      ) : (
        <Stack divider={<Divider />}>
          {closures.map(c => (
            <Box key={c._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.75 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography sx={{ fontWeight: 600, color: '#0f172a' }}>{c.name}</Typography>
                  {c.isActiveNow && (
                    <Chip size="small" label="Closed now" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 700 }} />
                  )}
                  <Chip
                    size="small"
                    label={c.menuId ? `${c.menuName || 'Menu'} only` : 'Whole restaurant'}
                    sx={{ height: 20, fontSize: '0.7rem', bgcolor: c.menuId ? '#eef2ff' : '#f1f5f9', color: c.menuId ? '#4338ca' : '#475569' }}
                  />
                </Stack>
                <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: 0.25 }}>
                  {c.dateLabel}
                  {!c.allDay && c.startTime ? ` · ${c.startTime} to ${c.endTime}` : ''}
                  {c.note ? ` · ${c.note}` : ''}
                </Typography>
              </Box>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => handleDelete(c)}><DeleteIcon fontSize="small" sx={{ color: '#dc2626' }} /></IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Edit closing' : 'Add closing'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus fullWidth margin="normal" label="Name" value={form.name}
            onChange={e => setField({ name: e.target.value })}
            placeholder="e.g., Christmas Day, Staff holiday"
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField
              fullWidth type="date" label="First day closed" value={form.startDate}
              onChange={e => {
                const startDate = e.target.value
                // Keep the range valid while typing: a single-day closing is the
                // common case, so the end date follows until it is set apart.
                setField({ startDate, endDate: !form.endDate || form.endDate < startDate ? startDate : form.endDate })
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth type="date" label="Last day closed" value={form.endDate}
              onChange={e => setField({ endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Same as the first day for a one-day closing."
            />
          </Box>

          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={<Switch checked={form.allDay} onChange={e => setField({ allDay: e.target.checked })} />}
            label="Closed all day"
          />
          {!form.allDay && (
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <TextField
                fullWidth type="time" label="Closed from" value={form.startTime}
                onChange={e => setField({ startTime: e.target.value })} InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth type="time" label="Closed until" value={form.endTime}
                onChange={e => setField({ endTime: e.target.value })} InputLabelProps={{ shrink: true }}
                helperText="Applies on every day of the range."
              />
            </Box>
          )}

          <TextField
            select fullWidth margin="normal" label="Applies to" value={form.menuId}
            onChange={e => setField({ menuId: e.target.value })}
            helperText="Whole restaurant stops all ordering. A single menu just takes that menu off the storefront."
          >
            <MenuItem value="">Whole restaurant</MenuItem>
            {menus.map(m => (
              <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth margin="normal" label="Note (optional)" value={form.note}
            onChange={e => setField({ note: e.target.value })}
            placeholder="Shown to your team, not to customers"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', backgroundColor: '#1e40af' }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : editing ? 'Save' : 'Add closing'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default ClosuresPanel
